import * as THREE from "three";
import { GPUComputationRenderer, Variable } from "three/examples/jsm/misc/GPUComputationRenderer.js";
import {
  CAMERA_FOV_2D,
  CLOCK_PERIOD_DEFAULT,
  COLLISION_SUBSTEPS,
  CONTACT_RELEASE_FACTOR,
  EMIT_BOUNCE_DEFAULT,
  EMIT_DRAG_DEFAULT,
  EMIT_FRICTION_DEFAULT,
  EMITTER_SEED,
  FALL_KILL_DIST_MAX,
  FALL_KILL_LIFE_DIV_DEFAULT,
  FIXED_DT,
  GRAVITY,
  GRAVITY_CONSTANT,
  GRAVITY_SOFTENING,
  GRID_IDX_TEX_W,
  IMPACT_MAX,
  CONTACT_RING_SIZE,
  CONTACT_READBACK_PACK,
  CONTACT_FORCE_HALF,
  IMPACT_MIN,
  KEY_LIGHT_DIR,
  LIFESPAN_MAX,
  LIFESPAN_MIN,
  MAX_DELTA,
  MAX_EMITTERS,
  MAX_OBJECTS,
  PARTICLE_AMBIENT,
  PARTICLE_COLOR,
  PARTICLE_COUNT,
  PARTICLE_GLOW_COLOR,
  PARTICLE_GLOW_DECAY_TAU,
  PARTICLE_GLOW_PEAK,
  PARTICLE_RIM,
  PARTICLE_SHININESS,
  PARTICLE_SPECULAR,
  PARTICLE_TEXTURE_WIDTH,
  PARTICLE_WORLD_SIZE,
  RAIL_GAP_RATIO,
  RAIL_SAMPLES,
  RAIL_TUBE_FACTOR,
  RAIL_TUBE_MIN,
  SDF_EPS,
  VIEW_HEIGHT,
} from "./constants";
import { Emitter } from "./emitters";
import { cellSec, phaseAt, wrappedClockTime } from "./clock";
import { computeEmitCount, computeGridEmitCount, computeSeqEmit, emitWindowStart, gatedRate } from "./emitPhase";
import { ONSET_DIAG, recordSeqEmit } from "./onsetDiag";
import { CollisionUniforms } from "./objects";

const DEG = 180 / Math.PI;

const FALL_KILL_DIST_OFF = 1e9;

const INJ_SPEED_SPREAD = 0.1; // fraction of the inherited impact speed (±5%)

function glslFloat(value: number): string {
  return Number.isInteger(value) ? value.toFixed(1) : String(value);
}

const HASH_GLSL = `
float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}
`;

const OBJECT_UNIFORMS_GLSL = `
uniform int uObjCount;
uniform int uAlwaysCount;
uniform int uUseGrid;
uniform sampler2D uObjTex;
uniform sampler2D uRailTex;
uniform sampler2D uGridHeadTex;
uniform sampler2D uGridIdxTex;
uniform vec2 uGridOrigin;
uniform float uCellSize;
uniform int uGridW;
uniform int uGridH;

const int GRID_IDX_W = ${GRID_IDX_TEX_W};

vec4 objA(int i) { return texelFetch(uObjTex, ivec2(0, i), 0); }
vec4 objB(int i) { return texelFetch(uObjTex, ivec2(1, i), 0); }
vec4 objC(int i) { return texelFetch(uObjTex, ivec2(2, i), 0); }
vec4 objD(int i) { return texelFetch(uObjTex, ivec2(3, i), 0); }
vec4 objE(int i) { return texelFetch(uObjTex, ivec2(4, i), 0); }
vec4 objF(int i) { return texelFetch(uObjTex, ivec2(5, i), 0); }

ivec2 gridCell(vec3 p) {
  return ivec2(floor((p.xy - uGridOrigin) / uCellSize));
}

int gridSlotAt(int lin) {
  return int(texelFetch(uGridIdxTex, ivec2(lin % GRID_IDX_W, lin / GRID_IDX_W), 0).r + 0.5);
}
`;

const GRAVITY_GLSL = `
const float GRAVITY_G = ${glslFloat(GRAVITY_CONSTANT)};
const float GRAVITY_SOFT2 = ${glslFloat(GRAVITY_SOFTENING * GRAVITY_SOFTENING)};

vec3 attractorAccel(vec3 p) {
  vec3 a = vec3(0.0);
  int cnt = (uUseGrid == 1) ? uAlwaysCount : uObjCount;
  for (int i = 0; i < ${MAX_OBJECTS}; i++) {
    if (i >= cnt) break;
    vec3 d = objA(i).xyz - p;
    float m = objC(i).w;
    float invr = inversesqrt(dot(d, d) + GRAVITY_SOFT2);
    a += GRAVITY_G * m * d * (invr * invr * invr);
  }
  return a;
}
`;

const FIELD_GLSL = `
vec3 fieldAccel(vec3 p, vec3 v) {
  vec3 a = vec3(0.0);
  int cnt = (uUseGrid == 1) ? uAlwaysCount : uObjCount;
  for (int i = 0; i < ${MAX_OBJECTS}; i++) {
    if (i >= cnt) break;
    if (objA(i).w < 2.5 || objA(i).w > 3.5) continue;
    vec4 q = objE(i);
    vec3 lp = qrotate(vec4(-q.xyz, q.w), p - objA(i).xyz);
    vec3 lv = qrotate(vec4(-q.xyz, q.w), v);
    vec2 d = lp.xy;
    float R = objB(i).x;
    float dist2 = dot(d, d);
    if (dist2 > R * R) continue;
    float fade = objD(i).z;
    float falloff = 1.0;
    if (fade > 0.0) {
      falloff = 1.0 - smoothstep(R * (1.0 - fade), R, sqrt(dist2));
    }
    float mode = objB(i).y;
    vec3 lf;
    if (mode < 0.5) {
      float s = objB(i).z;
      lf = s * vec3(lv.y, -lv.x, 0.0);
    } else {
      float w = objB(i).z;
      float k = objB(i).w;
      vec3 u = w * vec3(-d.y, d.x, 0.0);
      lf = k * (u - lv);
    }
    a += falloff * qrotate(q, lf);
  }
  return a;
}
`;

const SDF_GLSL = `
const float SDF_EPS = ${glslFloat(SDF_EPS)};
uniform float uParticleRadius;
uniform float uCollisionEnabled;

vec3 qrotate(vec4 q, vec3 v) {
  return v + 2.0 * cross(q.xyz, cross(q.xyz, v) + q.w * v);
}

float sdBox(vec3 p, vec3 b) {
  vec3 q = abs(p) - b;
  return length(max(q, 0.0)) + min(max(q.x, max(q.y, q.z)), 0.0);
}

float sdSphere(vec3 p, float r) {
  return length(p) - r;
}

// funnel SDF: a (radius, height) cross-section segment shelled by half-thickness
// t, revolved about Y and cut flat at y=±h. arcHalf (radians, from objC(i).y)
// keeps only the +X-symmetric sector [-arcHalf, +arcHalf] of the revolution:
// the shell is z-mirror symmetric, so p.z folds to |z| and only the +arcHalf
// cut plane needs testing. The sine-sign test (side > 0) detects out-of-sector
// points without atan; for those the nearest solid point lies on the cut face,
// so the in-plane radial coordinate becomes dot(d, u) and the final distance
// composes the planar cross-section distance with the off-plane distance
// (length(vec2(...))), giving the cut end an exact rounded-edge field — a
// plane-clamp-only distance would let particles tunnel at the rim. arcHalf at
// or above π skips the sector branch entirely, keeping the full-revolution
// result bit-identical to the pre-arc shader.
float sdFunnel(vec3 p, float rB, float rT, float h, float t, float arcHalf) {
  float r = length(p.xz);
  float w = -1.0;
  if (arcHalf < ${glslFloat(Math.PI - 1e-3)}) {
    vec2 d = vec2(p.x, abs(p.z));
    vec2 u = vec2(cos(arcHalf), sin(arcHalf));
    float side = u.x * d.y - u.y * d.x;
    if (side > 0.0) {
      r = dot(d, u);
      w = side;
    }
  }
  vec2 a = vec2(rB, -h);
  vec2 e = vec2(rT, h) - a;
  vec2 q = vec2(r, p.y);
  float proj = clamp(dot(q - a, e) / dot(e, e), 0.0, 1.0);
  float wall = length(q - (a + e * proj)) - t;
  float cap = abs(p.y) - h;
  float sect = min(max(wall, cap), 0.0) + length(max(vec2(wall, cap), 0.0));
  return w < 0.0 ? sect : length(vec2(max(sect, 0.0), w));
}

const float RAIL_TUBE_FACTOR_C = ${glslFloat(RAIL_TUBE_FACTOR)};
const float RAIL_TUBE_MIN_C = ${glslFloat(RAIL_TUBE_MIN)};
const float RAIL_GAP_RATIO = ${glslFloat(RAIL_GAP_RATIO)};
const float RAIL_SAMPLES_F = ${glslFloat(RAIL_SAMPLES)};

// rail SDF: the wall pair straddles the curve plane at z = +/-halfGap, so the
// distance is the in-plane distance to the center polyline combined with
// (|z| - halfGap) — exactly the nearer of the two offset tubes. A plane-locked
// particle always reaches contact (halfGap = RAIL_GAP_RATIO * particle radius
// stays below the contact reach), riding the cradle between the tubes. The SDF
// is offset by tubeRadius so the contact surface sits exactly at the thin
// visible tube plus the particle radius (formulas mirror
// railTubeRadius/railHalfGap in objects.ts). The closest point past either
// polyline end returns no
// surface, so particles slide out at the ends. t is the normalized arc
// position of the closest point (samples are arc-length uniform).
// texelFetch (not texture2D): implicit-derivative sampling is undefined inside
// this varying-iteration loop, and the texel grid is addressed exactly anyway.
// cutoff is the caller's running nearest distance: objB holds the local bounding
// circle (packed in writeColliderSlot), so when the distance to that circle
// already exceeds cutoff this rail cannot win and the sample loop is skipped.
// The circle radius is inflated CPU-side, so the bound never over-rejects and
// the result stays bit-identical to the unculled scan.
float sdRail(int i, vec3 lp, out float t, float cutoff) {
  t = 0.0;
  float tubeR = max(uParticleRadius * RAIL_TUBE_FACTOR_C, RAIL_TUBE_MIN_C);
  float halfGap = RAIL_GAP_RATIO * uParticleRadius;
  float dz = abs(lp.z) - halfGap;
  vec3 bnd = objB(i).xyz;
  float dxy = max(length(lp.xy - bnd.xy) - bnd.z, 0.0);
  if (sqrt(dxy * dxy + dz * dz) - tubeR >= cutoff) return 1e9;
  vec2 prev = texelFetch(uRailTex, ivec2(0, i), 0).xy;
  float dminSq = 1e18;
  float bestSeg = 0.0;
  float bestProj = 0.0;
  float bestRaw = 0.0;
  for (int j = 1; j < ${RAIL_SAMPLES}; j++) {
    vec2 pt = texelFetch(uRailTex, ivec2(j, i), 0).xy;
    vec2 e = pt - prev;
    float hRaw = dot(lp.xy - prev, e) / max(dot(e, e), 1e-12);
    float h = clamp(hRaw, 0.0, 1.0);
    vec2 diff = lp.xy - (prev + e * h);
    float distSq = dot(diff, diff);
    if (distSq < dminSq) {
      dminSq = distSq;
      bestSeg = float(j - 1);
      bestProj = h;
      bestRaw = hRaw;
    }
    prev = pt;
  }
  bool beforeStart = bestSeg < 0.5 && bestRaw < 0.0;
  bool pastEnd = bestSeg > RAIL_SAMPLES_F - 2.5 && bestRaw > 1.0;
  if (beforeStart || pastEnd) return 1e9;
  t = (bestSeg + bestProj) / (RAIL_SAMPLES_F - 1.0);
  return sqrt(dminSq + dz * dz) - tubeR;
}

float evalObject(int i, vec3 p, out float t, float cutoff) {
  t = 0.0;
  if (objD(i).w < 0.5) return 1e9;
  vec3 pos = objA(i).xyz;
  float type = objA(i).w;
  if (type > 2.5 && type < 3.5) return 1e9;
  vec4 q = objE(i);
  vec3 lp = qrotate(vec4(-q.xyz, q.w), p - pos);
  if (type > 3.5) return sdRail(i, lp, t, cutoff);
  vec4 b = objB(i);
  if (type < 0.5) return sdBox(lp, b.xyz);
  if (type < 1.5) return sdSphere(lp, b.x);
  return sdFunnel(lp, b.x, b.y, b.z, b.w, objC(i).y);
}

// Swept evaluation against collider i in its own moving frame. sweepDt is the
// time remaining to the end of the integration frame at this sample (= dt*(1-frac)),
// so shifting the sample point by objF(i)*sweepDt reconstructs the collider's earlier
// in-frame position (objA is the end-of-frame position; objF*dt is its frame
// displacement). A particle that the collider surface overtakes thus still registers
// contact instead of tunnelling. sweepDt=0 reduces exactly to evalObject (static path).
float evalObjectSwept(int i, vec3 p, float sweepDt, out float t, float cutoff) {
  return evalObject(i, p + objF(i).xyz * sweepDt, t, cutoff);
}

// uUseGrid selects the collider candidate set the scene reductions scan. Grid
// mode (production) tests the always-test prefix [0, uAlwaysCount) — every force
// source and large collider — plus the grid colliders binned into the query
// point's 3×3 cell neighbourhood; brute mode (debug A/B) tests every collider
// [0, uObjCount). The reductions stay pure nearest-of-candidates, so the two
// paths match bit-for-bit as long as the candidate set holds every collider
// within contact reach of the query point — guaranteed when one substep step
// stays inside the 3×3 neighbourhood (cellSize >> particle radius; the TOI
// safety bound the A/B toggle verifies).
float sceneSDF(vec3 p) {
  float d = 1e9;
  float tDump;
  if (uUseGrid == 0) {
    for (int i = 0; i < ${MAX_OBJECTS}; i++) {
      if (i >= uObjCount) break;
      d = min(d, evalObject(i, p, tDump, d));
    }
    return d;
  }
  for (int i = 0; i < ${MAX_OBJECTS}; i++) {
    if (i >= uAlwaysCount) break;
    d = min(d, evalObject(i, p, tDump, d));
  }
  ivec2 c = gridCell(p);
  for (int dy = -1; dy <= 1; dy++) {
    for (int dx = -1; dx <= 1; dx++) {
      int cx = c.x + dx;
      int cy = c.y + dy;
      if (cx < 0 || cy < 0 || cx >= uGridW || cy >= uGridH) continue;
      vec4 h = texelFetch(uGridHeadTex, ivec2(cx, cy), 0);
      int start = int(h.r + 0.5);
      int cnt = int(h.g + 0.5);
      for (int n = 0; n < ${MAX_OBJECTS}; n++) {
        if (n >= cnt) break;
        d = min(d, evalObject(gridSlotAt(start + n), p, tDump, d));
      }
    }
  }
  return d;
}

void considerNearest(int i, vec3 p, float sweepDt, inout float dmin, inout float rest, inout float oidx, inout float ofric, inout float ot) {
  float ti;
  float di = evalObjectSwept(i, p, sweepDt, ti, dmin);
  if (di < dmin) { dmin = di; rest = objC(i).x; oidx = float(i); ofric = objD(i).x; ot = ti; }
}

float nearestRestitution(vec3 p, float sweepDt, out float dmin, out float oidx, out float ofric, out float ot) {
  dmin = 1e9;
  float rest = 0.0;
  oidx = -1.0;
  ofric = 0.0;
  ot = 0.0;
  if (uCollisionEnabled < 0.5) return rest;
  if (uUseGrid == 0) {
    for (int i = 0; i < ${MAX_OBJECTS}; i++) {
      if (i >= uObjCount) break;
      considerNearest(i, p, sweepDt, dmin, rest, oidx, ofric, ot);
    }
    return rest;
  }
  for (int i = 0; i < ${MAX_OBJECTS}; i++) {
    if (i >= uAlwaysCount) break;
    considerNearest(i, p, sweepDt, dmin, rest, oidx, ofric, ot);
  }
  ivec2 c = gridCell(p);
  for (int dy = -1; dy <= 1; dy++) {
    for (int dx = -1; dx <= 1; dx++) {
      int cx = c.x + dx;
      int cy = c.y + dy;
      if (cx < 0 || cy < 0 || cx >= uGridW || cy >= uGridH) continue;
      vec4 h = texelFetch(uGridHeadTex, ivec2(cx, cy), 0);
      int start = int(h.r + 0.5);
      int cnt = int(h.g + 0.5);
      for (int n = 0; n < ${MAX_OBJECTS}; n++) {
        if (n >= cnt) break;
        considerNearest(gridSlotAt(start + n), p, sweepDt, dmin, rest, oidx, ofric, ot);
      }
    }
  }
  return rest;
}

vec3 resolveCollision(vec3 p, vec3 v, float dt, float emBounce, float emFric, out float hitIdx, out float hitImpact, out float hitT, out float hitFrac, out vec3 hitPoint, out vec3 hitNormal, out float hitPenetration) {
  hitIdx = -1.0;
  hitImpact = 0.0;
  hitT = 0.0;
  hitFrac = 0.0;
  hitPoint = p;
  hitNormal = vec3(0.0);
  hitPenetration = 0.0;
  if (uCollisionEnabled < 0.5) return v;
  if (uObjCount == 0) return v;

  float contactRadius = uParticleRadius;
  vec3 disp = v * dt;
  vec3 hp = p;
  float dmin = 1e9;
  float rest = 0.0;
  float friction = 0.0;
  bool hit = false;
  float dsPrev = 1e9;
  for (int s = 0; s <= ${COLLISION_SUBSTEPS}; s++) {
    float frac = float(s) / float(${COLLISION_SUBSTEPS});
    vec3 sp = p + disp * frac;
    float sweepDt = dt * (1.0 - frac);
    float ds;
    float oidx;
    float ofric;
    float ot;
    float r = nearestRestitution(sp, sweepDt, ds, oidx, ofric, ot);
    if (ds < contactRadius) {
      float subStep = 1.0 / float(${COLLISION_SUBSTEPS});
      if (s == 0 || dsPrev <= contactRadius || dsPrev > 1e8) {
        hitFrac = frac;
        dmin = ds;
      } else {
        float refine = clamp((dsPrev - contactRadius) / (dsPrev - ds), 0.0, 1.0);
        hitFrac = frac - subStep * (1.0 - refine);
        dmin = contactRadius;
      }
      hp = p + disp * hitFrac;
      rest = r * emBounce; friction = ofric * emFric; hit = true; hitIdx = oidx; hitT = ot; break;
    }
    dsPrev = ds;
  }
  if (!hit) return v;
  hitPoint = hp;

  int hi = int(hitIdx);
  vec2 e = vec2(SDF_EPS, 0.0);
  float tDump;
  float sweepHit = dt * (1.0 - hitFrac);
  vec3 g = vec3(
    evalObjectSwept(hi, hp + e.xyy, sweepHit, tDump, 1e9) - evalObjectSwept(hi, hp - e.xyy, sweepHit, tDump, 1e9),
    evalObjectSwept(hi, hp + e.yxy, sweepHit, tDump, 1e9) - evalObjectSwept(hi, hp - e.yxy, sweepHit, tDump, 1e9),
    evalObjectSwept(hi, hp + e.yyx, sweepHit, tDump, 1e9) - evalObjectSwept(hi, hp - e.yyx, sweepHit, tDump, 1e9)
  );
  float gl = length(g);
  if (gl < 1e-5) return v;
  vec3 n = g / gl;
  hitNormal = n;
  hitPenetration = max(contactRadius - dmin, 0.0);
  vec3 vs = objF(hi).xyz;
  vec3 vr = v - vs;
  float vn = dot(vr, n);
  hitImpact = abs(vn);
  vec3 vTangent = (vr - vn * n) * (1.0 - friction);
  float vnOut = (vn < 0.0) ? -rest * vn : max(vn, 0.0);
  return vs + vTangent + vnOut * n;
}
`;

const POSITION_FRAGMENT = `
${HASH_GLSL}
${OBJECT_UNIFORMS_GLSL}
${SDF_GLSL}
${GRAVITY_GLSL}
${FIELD_GLSL}

uniform float uDt;
uniform vec3 uGravity;
uniform int uEmitterCount;
uniform vec3 uEmitterPos[${MAX_EMITTERS}];
uniform int uEmitStart[${MAX_EMITTERS}];
uniform int uEmitCount[${MAX_EMITTERS}];
uniform float uEmitBounce[${MAX_EMITTERS}];
uniform float uEmitFriction[${MAX_EMITTERS}];
uniform float uZLock;
uniform float uFallKillDist;
uniform float uFallKillLifeDiv;

void main() {
  vec2 uv = gl_FragCoord.xy / resolution.xy;
  vec4 posData = texture2D( texturePosition, uv );
  vec4 velData = texture2D( textureVelocity, uv );

  vec4 glowData = texture2D( textureGlow, uv );
  float lifespan = glowData.y;
  float age = posData.w + uDt;
  int idx = int(gl_FragCoord.y) * ${PARTICLE_TEXTURE_WIDTH} + int(gl_FragCoord.x);

  vec2 idData = texture2D( textureEmitterId, uv ).xy;
  int eid = int(idData.x + 0.5);
  float emBounce = uEmitBounce[eid];
  float emFric = uEmitFriction[eid];

  vec3 vNew = velData.xyz + (uGravity + attractorAccel(posData.xyz) + fieldAccel(posData.xyz, velData.xyz)) * uDt;
  float hitIdx;
  float hitImpact;
  float hitT;
  float hitFrac;
  vec3 hitPoint;
  vec3 hitNormal;
  float hitPenetration;
  vNew = resolveCollision(posData.xyz, vNew, uDt, emBounce, emFric, hitIdx, hitImpact, hitT, hitFrac, hitPoint, hitNormal, hitPenetration);
  vec3 p = (hitIdx >= 0.0)
    ? hitPoint + hitNormal * hitPenetration + vNew * uDt * (1.0 - hitFrac)
    : posData.xyz + vNew * uDt;

  bool killed = false;
  if (hitIdx >= 0.0) {
    killed = objD(int(hitIdx)).y > 0.5;
  }
  bool fellKilled = (idData.y - p.y >= uFallKillDist) && (age - glowData.z >= lifespan / uFallKillLifeDiv);

  bool emit = false;
  vec3 emitPos = vec3(0.0);
  for (int e = 0; e < ${MAX_EMITTERS}; e++) {
    if (e >= uEmitterCount) break;
    int emitRel = idx - uEmitStart[e];
    if (emitRel < 0) emitRel += ${PARTICLE_COUNT};
    if (emitRel < uEmitCount[e]) { emit = true; emitPos = uEmitterPos[e]; break; }
  }
  if (emit) {
    p = emitPos;
    age = 0.0;
  } else if (age >= lifespan || killed || fellKilled) {
    p = vec3(1e6, 1e6, 0.0);
    age = lifespan + 1.0;
  }

  p.z *= (1.0 - uZLock);
  gl_FragColor = vec4(p, age);
}
`;

const VELOCITY_FRAGMENT = `
${HASH_GLSL}
${OBJECT_UNIFORMS_GLSL}
${SDF_GLSL}
${GRAVITY_GLSL}
${FIELD_GLSL}

uniform float uDt;
uniform float uClockTimeWrapped;
uniform vec3 uGravity;
uniform int uEmitterCount;
uniform int uEmitStart[${MAX_EMITTERS}];
uniform int uEmitCount[${MAX_EMITTERS}];
uniform float uEmitCone[${MAX_EMITTERS}];
uniform vec4 uEmitQuat[${MAX_EMITTERS}];
uniform float uEmitSpeedMin[${MAX_EMITTERS}];
uniform float uEmitSpeedMax[${MAX_EMITTERS}];
uniform float uEmitBounce[${MAX_EMITTERS}];
uniform float uEmitFriction[${MAX_EMITTERS}];
uniform float uEmitDrag[${MAX_EMITTERS}];
uniform float uZLock;

const float SEED = ${glslFloat(EMITTER_SEED)};
const float IMPACT_MAX = ${glslFloat(IMPACT_MAX)};
const float IMPACT_MIN = ${glslFloat(IMPACT_MIN)};
const float CONTACT_RELEASE_FACTOR = ${glslFloat(CONTACT_RELEASE_FACTOR)};

vec3 emitVelocity(vec2 uv, float t, vec4 q, float cone, float speedMin, float speedMax) {
  float r1 = hash21(uv + SEED + t);
  float r2 = hash21(uv * 1.731 + SEED + t * 0.37);
  float r3 = hash21(uv * 2.917 + SEED + t * 0.53);
  float a1 = (r1 - 0.5) * 2.0 * cone;
  float a2 = (r3 - 0.5) * 2.0 * cone * (1.0 - uZLock);
  float speed = speedMin + r2 * (speedMax - speedMin);
  vec3 local = vec3(sin(a1) * cos(a2), cos(a1) * cos(a2), sin(a2));
  return qrotate(q, local) * speed;
}

void main() {
  vec2 uv = gl_FragCoord.xy / resolution.xy;
  vec4 posData = texture2D( texturePosition, uv );
  vec4 velData = texture2D( textureVelocity, uv );

  float lifespan = texture2D( textureGlow, uv ).y;
  float age = posData.w + uDt;
  int idx = int(gl_FragCoord.y) * ${PARTICLE_TEXTURE_WIDTH} + int(gl_FragCoord.x);

  vec3 v = velData.xyz + uGravity * uDt;
  float contactW = 0.0;

  bool emit = false;
  for (int e = 0; e < ${MAX_EMITTERS}; e++) {
    if (e >= uEmitterCount) break;
    int emitRel = idx - uEmitStart[e];
    if (emitRel < 0) emitRel += ${PARTICLE_COUNT};
    if (emitRel < uEmitCount[e]) {
      v = emitVelocity(uv, fract(uClockTimeWrapped * 0.137), uEmitQuat[e], uEmitCone[e], uEmitSpeedMin[e], uEmitSpeedMax[e]);
      emit = true;
      break;
    }
  }
  if (emit) {
  } else if (age >= lifespan) {
    v = vec3(0.0);
  } else {
    int eid = int(texture2D( textureEmitterId, uv ).x + 0.5);
    float emBounce = uEmitBounce[eid];
    float emFric = uEmitFriction[eid];
    v += (attractorAccel(posData.xyz) + fieldAccel(posData.xyz, velData.xyz)) * uDt;
    float hitIdx;
    float hitImpact;
    float hitT;
    float hitFrac;
    vec3 hitPoint;
    vec3 hitNormal;
    float hitPenetration;
    v = resolveCollision(posData.xyz, v, uDt, emBounce, emFric, hitIdx, hitImpact, hitT, hitFrac, hitPoint, hitNormal, hitPenetration);
    v *= exp(-uEmitDrag[eid] * uDt);
    float nearDmin;
    float nearIdx;
    float nearFric;
    float nearT;
    nearestRestitution(posData.xyz, 0.0, nearDmin, nearIdx, nearFric, nearT);
    float releaseRadius = uParticleRadius * CONTACT_RELEASE_FACTOR;
    bool wasNear = (velData.w != 0.0);
    bool inContact = (hitIdx >= 0.0);
    if (inContact && !wasNear && hitImpact >= IMPACT_MIN) {
      // impN must stay strictly < 1.0 so it cannot carry into the integer part that encodes hitIdx
      float impN = clamp(hitImpact / IMPACT_MAX, 0.0, 0.999);
      contactW = hitIdx + 1.0 + impN;
    } else if (inContact || (wasNear && nearDmin < releaseRadius)) {
      // the release band only extends an already-near contact (never pre-flags an approaching particle), so a clinging/bouncing particle stays silent instead of re-onsetting each push-out cycle
      // the fraction carries the rail arc position t (0.0 for non-rail objects) for the sustained-voice readback
      contactW = -((inContact ? hitIdx : nearIdx) + 1.0 + clamp(inContact ? hitT : nearT, 0.0, 0.999));
    }
  }

  v.z *= (1.0 - uZLock);
  gl_FragColor = vec4(v, contactW);
}
`;

const GLOW_FRAGMENT = `
${HASH_GLSL}

uniform float uDt;
uniform int uEmitterCount;
uniform int uEmitStart[${MAX_EMITTERS}];
uniform int uEmitCount[${MAX_EMITTERS}];
uniform float uEmitLifeMin[${MAX_EMITTERS}];
uniform float uEmitLifeMax[${MAX_EMITTERS}];

const float GLOW_TAU = ${glslFloat(PARTICLE_GLOW_DECAY_TAU)};

void main() {
  vec2 uv = gl_FragCoord.xy / resolution.xy;
  vec4 glowData = texture2D( textureGlow, uv );
  float prevGlow = glowData.x;
  float onset = texture2D( textureVelocity, uv ).w;
  float age = texture2D( texturePosition, uv ).w;
  int idx = int(gl_FragCoord.y) * ${PARTICLE_TEXTURE_WIDTH} + int(gl_FragCoord.x);

  float lifespan = glowData.y;
  for (int e = 0; e < ${MAX_EMITTERS}; e++) {
    if (e >= uEmitterCount) break;
    int emitRel = idx - uEmitStart[e];
    if (emitRel < 0) emitRel += ${PARTICLE_COUNT};
    if (emitRel < uEmitCount[e]) {
      lifespan = uEmitLifeMin[e] + hash21(uv) * (uEmitLifeMax[e] - uEmitLifeMin[e]);
      break;
    }
  }

  float glow = prevGlow * exp(-uDt / GLOW_TAU);
  // contactW encodes onset as positive and sustained contact as negative, so
  // abs() catches both — a particle resting on an object keeps refreshing lastHit
  float lastHit = glowData.z;
  if (abs(onset) > 0.5) {
    lastHit = age;
  }
  if (onset > 0.5) {
    glow = 1.0;
  }
  if (age < uDt * 0.5 || age >= lifespan) {
    glow = 0.0;
    lastHit = 0.0;
  }
  gl_FragColor = vec4(glow, lifespan, lastHit, 1.0);
}
`;

const EMITTERID_FRAGMENT = `
uniform int uEmitterCount;
uniform int uEmitStart[${MAX_EMITTERS}];
uniform int uEmitCount[${MAX_EMITTERS}];
uniform vec3 uEmitterPos[${MAX_EMITTERS}];

void main() {
  vec2 uv = gl_FragCoord.xy / resolution.xy;
  vec2 idData = texture2D( textureEmitterId, uv ).xy;
  float id = idData.x;
  // spawn y is captured from uEmitterPos on the emit frame — the exact value the
  // position shader assigns as the respawn position — so it never shifts when the
  // emitter later moves
  float spawnY = idData.y;
  int idx = int(gl_FragCoord.y) * ${PARTICLE_TEXTURE_WIDTH} + int(gl_FragCoord.x);

  for (int e = 0; e < ${MAX_EMITTERS}; e++) {
    if (e >= uEmitterCount) break;
    int emitRel = idx - uEmitStart[e];
    if (emitRel < 0) emitRel += ${PARTICLE_COUNT};
    if (emitRel < uEmitCount[e]) {
      id = float(e);
      spawnY = uEmitterPos[e].y;
      break;
    }
  }

  gl_FragColor = vec4(id, spawnY, 0.0, 1.0);
}
`;

const RENDER_VERTEX = `
uniform sampler2D texturePosition;
uniform sampler2D textureGlow;
uniform float uPointWorldSize;
uniform float uPxPerWorld;
attribute vec2 aReference;
varying vec3 vViewCenter;
varying float vViewRadius;
varying float vGlow;

const float FADE_OUT_S = 0.2;
const float HALF_VIEW_HEIGHT = ${glslFloat(VIEW_HEIGHT / 2)};

void main() {
  vec4 posData = texture2D( texturePosition, aReference );
  vec4 glowData = texture2D( textureGlow, aReference );
  vGlow = glowData.x;
  float lifespan = glowData.y;
  float fade = clamp((lifespan - posData.w) / FADE_OUT_S, 0.0, 1.0);
  vec4 mvPosition = modelViewMatrix * vec4(posData.xyz, 1.0);
  vViewCenter = mvPosition.xyz;
  vViewRadius = uPointWorldSize * 0.5 * fade;
  gl_Position = projectionMatrix * mvPosition;
  gl_PointSize = uPointWorldSize * fade * projectionMatrix[1][1] * uPxPerWorld * HALF_VIEW_HEIGHT / max(gl_Position.w, 0.001);
}
`;

const RENDER_FRAGMENT = `
uniform mat4 projectionMatrix;
uniform vec3 uColor;
uniform vec3 uLightDir;
uniform float uAmbient;
uniform float uSpecular;
uniform float uShininess;
uniform float uRim;
uniform vec3 uGlowColor;
uniform float uOpacity;
varying vec3 vViewCenter;
varying float vViewRadius;
varying float vGlow;

void main() {
  vec2 c = (gl_PointCoord - vec2(0.5)) * 2.0;
  c.y = -c.y;
  float d2 = dot(c, c);
  if (d2 > 1.0) discard;
  float zc = sqrt(1.0 - d2);
  vec3 normal = vec3(c, zc);

  vec3 viewPos = vViewCenter + normal * vViewRadius;
  vec4 clip = projectionMatrix * vec4(viewPos, 1.0);
  gl_FragDepth = (clip.z / clip.w) * 0.5 + 0.5;

  vec3 L = normalize((viewMatrix * vec4(uLightDir, 0.0)).xyz);
  float diff = max(dot(normal, L), 0.0);
  vec3 H = normalize(L + vec3(0.0, 0.0, 1.0));
  float spec = pow(max(dot(normal, H), 0.0), uShininess) * uSpecular;
  float rim = pow(1.0 - zc, 3.0) * uRim;

  vec3 lit = uColor * (uAmbient + diff) + vec3(spec) + uColor * rim;
  lit += uGlowColor * vGlow;
  gl_FragColor = vec4(lit, uOpacity);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

const SCATTER_VERTEX = `
uniform sampler2D textureVelocity;
attribute vec2 aReference;
varying vec4 vOut;

const float MAX_OBJECTS_F = ${glslFloat(MAX_OBJECTS)};

// positive w = onset (accumulates R = onset count, G = impact sum); negative w =
// sustained contact (accumulates B = arc-position-t sum, A = contact count, the
// per-frame state the rail voice reads). One-one blending adds vOut verbatim.
void main() {
  float idx;
  float w = texture2D(textureVelocity, aReference).w;
  if (w > 0.5) {
    idx = floor(w) - 1.0;
    vOut = vec4(1.0, fract(w), 0.0, 0.0);
  } else if (w < -0.5) {
    float aw = -w;
    idx = floor(aw) - 1.0;
    vOut = vec4(0.0, 0.0, fract(aw), 1.0);
  } else {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    gl_PointSize = 0.0;
    vOut = vec4(0.0);
    return;
  }
  float x = (idx + 0.5) / MAX_OBJECTS_F * 2.0 - 1.0;
  gl_Position = vec4(x, 0.0, 0.0, 1.0);
  gl_PointSize = 1.0;
}
`;

const SCATTER_FRAGMENT = `
varying vec4 vOut;
void main() {
  gl_FragColor = vOut;
}
`;

const FULLSCREEN_QUAD_VERTEX = `
void main() {
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

const CONTACT_ENCODE_FRAGMENT = `
precision highp float;
precision highp int;
uniform sampler2D uFloatTex;
out vec4 outColor;
const float MAX_OBJECTS_F = ${glslFloat(MAX_OBJECTS)};

// Packs one float channel of the float contact RT into a texel's RGBA8 as its
// little-endian IEEE-754 bytes, so a CPU Float32Array view over the read-back
// bytes reconstructs it exactly. Output is 4 texels per object (R,G,B,A of the
// source), so the byte layout matches the float RT's channel order. iOS Safari
// cannot readPixels a float RT but reads RGBA8, so this pass is its bridge.
void main() {
  float px = floor(gl_FragCoord.x);
  float obj = floor(px / 4.0);
  float ch = px - obj * 4.0;
  vec4 v = texture(uFloatTex, vec2((obj + 0.5) / MAX_OBJECTS_F, 0.5));
  float f = ch < 0.5 ? v.r : (ch < 1.5 ? v.g : (ch < 2.5 ? v.b : v.a));
  uint bits = floatBitsToUint(f);
  outColor = vec4(
    float(bits & 255u),
    float((bits >> 8) & 255u),
    float((bits >> 16) & 255u),
    float((bits >> 24) & 255u)
  ) / 255.0;
}
`;

function fillInitialTextures(positionTexture: THREE.DataTexture, velocityTexture: THREE.DataTexture): void {
  const position = positionTexture.image.data as Float32Array;
  const velocity = velocityTexture.image.data as Float32Array;

  for (let i = 0; i < PARTICLE_COUNT; i++) {
    const k = i * 4;

    position[k] = 1e6;
    position[k + 1] = 1e6;
    position[k + 2] = 0;
    position[k + 3] = LIFESPAN_MAX + 1;

    velocity[k] = 0;
    velocity[k + 1] = 0;
    velocity[k + 2] = 0;
    velocity[k + 3] = 0;
  }
}

function buildReferenceGeometry(): THREE.BufferGeometry {
  const positions = new Float32Array(PARTICLE_COUNT * 3);
  const references = new Float32Array(PARTICLE_COUNT * 2);

  for (let i = 0; i < PARTICLE_COUNT; i++) {
    references[i * 2] = (i % PARTICLE_TEXTURE_WIDTH) / PARTICLE_TEXTURE_WIDTH;
    references[i * 2 + 1] = Math.floor(i / PARTICLE_TEXTURE_WIDTH) / PARTICLE_TEXTURE_WIDTH;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("aReference", new THREE.BufferAttribute(references, 2));
  return geometry;
}

export const PARTICLE_MATERIALS = ["matte", "metal", "glossy"] as const;
export type ParticleMaterial = (typeof PARTICLE_MATERIALS)[number];

interface ParticleMaterialParams {
  ambient: number;
  specular: number;
  shininess: number;
  rim: number;
}

/**
 * Shader-uniform presets for the particle look: the point shader has no PBR
 * metalness/roughness, so "metal" = strong sharp highlight with reduced
 * diffuse base + brighter rim, "glossy" = tight bright highlight over the
 * matte base. "matte" is the original tuning from constants.
 */
const PARTICLE_MATERIAL_PARAMS: Record<ParticleMaterial, ParticleMaterialParams> = {
  matte: { ambient: PARTICLE_AMBIENT, specular: PARTICLE_SPECULAR, shininess: PARTICLE_SHININESS, rim: PARTICLE_RIM },
  metal: { ambient: 0.22, specular: 1.3, shininess: 64, rim: 0.32 },
  glossy: { ambient: PARTICLE_AMBIENT, specular: 0.9, shininess: 96, rim: PARTICLE_RIM },
};

export class ParticleSystem {
  readonly points: THREE.Points;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly gpu: GPUComputationRenderer;
  private readonly positionVariable: Variable;
  private readonly velocityVariable: Variable;
  private readonly glowVariable: Variable;
  private readonly emitterIdVariable: Variable;
  private readonly renderMaterial: THREE.ShaderMaterial;
  private readonly scatterMaterial: THREE.ShaderMaterial;
  private readonly scatterPoints: THREE.Points;
  private readonly scatterScene: THREE.Scene;
  private readonly scatterCamera: THREE.Camera;
  private readonly contactRTs: readonly THREE.WebGLRenderTarget[];
  private readonly contactSlotPixels: readonly Float32Array[];
  private readonly contactSlotCounts: readonly Float32Array[];
  private readonly contactSlotImpacts: readonly Float32Array[];
  private readonly contactSlotRailTSums: readonly Float32Array[];
  private readonly contactSlotRailCounts: readonly Float32Array[];
  private readonly contactSlotInFlight: boolean[];
  private readonly contactSlotSubsteps: number[];
  private readonly contactSlotDeliverSubsteps: number[];
  private readonly contactSlotSimTime: number[];
  private readonly contactResolvedQueue: number[];
  private readonly contactCounts: Float32Array;
  private readonly contactImpacts: Float32Array;
  private readonly contactRailTSums: Float32Array;
  private readonly contactRailCounts: Float32Array;
  private contactWriteIndex: number;
  private lastDeliveredSubsteps: number = 0;
  private lastDeliveredSimTime: number = 0;
  private feedHeightPx: number | null = null;
  private readonly gravityUniform: { value: THREE.Vector3 };
  private readonly particleRadiusUniform: { value: number };
  private readonly collisionEnabledUniform: { value: number };
  private readonly emitterCountUniform: { value: number };
  private readonly emitStartUniform: { value: number[] };
  private readonly emitCountUniform: { value: number[] };
  private readonly emitterPosUniform: { value: THREE.Vector3[] };
  private readonly emitQuatUniform: { value: THREE.Vector4[] };
  private readonly emitConeUniform: { value: number[] };
  private readonly emitSpeedMinUniform: { value: number[] };
  private readonly emitSpeedMaxUniform: { value: number[] };
  private readonly emitLifeMinUniform: { value: number[] };
  private readonly emitLifeMaxUniform: { value: number[] };
  private readonly emitBounceUniform: { value: number[] };
  private readonly emitFrictionUniform: { value: number[] };
  private readonly emitDragUniform: { value: number[] };
  private readonly emitRates: number[];
  private readonly emitOffset: number[];
  private readonly emitExternal: number[];
  private readonly emitDriven: boolean[];
  private readonly emitGate: number[];
  private readonly emitSpeedMinBase: number[];
  private readonly emitSpeedMaxBase: number[];
  private readonly emitSpeedInj: (number | null)[];
  private readonly emitClockSync: boolean[];
  private readonly emitSeqMode: boolean[];
  private readonly emitSeqSteps: number[][];
  private readonly emitSeqNum: number[];
  private elapsed: number;
  private accumulator: number;
  private firstEverEmit: boolean = true;
  private firstSeqEmitAfterReset: boolean = false;
  private debugSimPaused: boolean = false;
  private debugReadbackOff: boolean = false;
  private contactReadGen: number = 0;
  private contactFloatCaps: { colorBufferFloat: boolean; floatLinear: boolean; floatBlend: boolean } = { colorBufferFloat: false, floatLinear: false, floatBlend: false };
  private contactReadPack: boolean = false;
  private contactEncodeRT: THREE.WebGLRenderTarget | null = null;
  private contactEncodeMaterial: THREE.ShaderMaterial | null = null;
  private contactEncodeScene: THREE.Scene | null = null;
  private readonly contactEncodeCamera: THREE.Camera = new THREE.Camera();
  private readonly contactSlotByteViews: readonly Uint8Array[];
  private nextEmitIndex: number;
  private clockPeriodSec: number;

  constructor(renderer: THREE.WebGLRenderer, collisionUniforms: CollisionUniforms) {
    this.renderer = renderer;
    this.elapsed = 0;
    this.accumulator = 0;
    this.nextEmitIndex = 0;
    this.clockPeriodSec = CLOCK_PERIOD_DEFAULT;
    this.emitterCountUniform = { value: 0 };
    this.emitStartUniform = { value: new Array<number>(MAX_EMITTERS).fill(0) };
    this.emitCountUniform = { value: new Array<number>(MAX_EMITTERS).fill(0) };
    this.emitterPosUniform = {
      value: Array.from({ length: MAX_EMITTERS }, (): THREE.Vector3 => new THREE.Vector3()),
    };
    this.emitQuatUniform = {
      value: Array.from({ length: MAX_EMITTERS }, (): THREE.Vector4 => new THREE.Vector4(0, 0, 0, 1)),
    };
    this.emitConeUniform = { value: new Array<number>(MAX_EMITTERS).fill(0) };
    this.emitSpeedMinUniform = { value: new Array<number>(MAX_EMITTERS).fill(0) };
    this.emitSpeedMaxUniform = { value: new Array<number>(MAX_EMITTERS).fill(0) };
    this.emitLifeMinUniform = { value: new Array<number>(MAX_EMITTERS).fill(LIFESPAN_MIN) };
    this.emitLifeMaxUniform = { value: new Array<number>(MAX_EMITTERS).fill(LIFESPAN_MAX) };
    this.emitBounceUniform = { value: new Array<number>(MAX_EMITTERS).fill(EMIT_BOUNCE_DEFAULT) };
    this.emitFrictionUniform = { value: new Array<number>(MAX_EMITTERS).fill(EMIT_FRICTION_DEFAULT) };
    this.emitDragUniform = { value: new Array<number>(MAX_EMITTERS).fill(EMIT_DRAG_DEFAULT) };
    this.emitRates = new Array<number>(MAX_EMITTERS).fill(0);
    this.emitOffset = new Array<number>(MAX_EMITTERS).fill(0);
    this.emitExternal = new Array<number>(MAX_EMITTERS).fill(0);
    this.emitDriven = new Array<boolean>(MAX_EMITTERS).fill(false);
    this.emitGate = new Array<number>(MAX_EMITTERS).fill(1);
    this.emitSpeedMinBase = new Array<number>(MAX_EMITTERS).fill(0);
    this.emitSpeedMaxBase = new Array<number>(MAX_EMITTERS).fill(0);
    this.emitSpeedInj = new Array<number | null>(MAX_EMITTERS).fill(null);
    this.emitClockSync = new Array<boolean>(MAX_EMITTERS).fill(false);
    this.emitSeqMode = new Array<boolean>(MAX_EMITTERS).fill(false);
    this.emitSeqSteps = Array.from({ length: MAX_EMITTERS }, (): number[] => []);
    this.emitSeqNum = new Array<number>(MAX_EMITTERS).fill(0);
    const gl = renderer.getContext();
    const floatBlend = gl.getExtension("EXT_float_blend") !== null;
    const colorBufferFloat = gl.getExtension("EXT_color_buffer_float") !== null;
    const floatLinear = gl.getExtension("OES_texture_float_linear") !== null;
    this.contactFloatCaps = { colorBufferFloat, floatLinear, floatBlend };
    console.log(`WebGL float caps: EXT_color_buffer_float=${colorBufferFloat} OES_texture_float_linear=${floatLinear} EXT_float_blend=${floatBlend}`);
    this.gpu = new GPUComputationRenderer(PARTICLE_TEXTURE_WIDTH, PARTICLE_TEXTURE_WIDTH, renderer);

    const positionTexture = this.gpu.createTexture();
    const velocityTexture = this.gpu.createTexture();
    const glowTexture = this.gpu.createTexture();
    const emitterIdTexture = this.gpu.createTexture();
    fillInitialTextures(positionTexture, velocityTexture);

    this.positionVariable = this.gpu.addVariable("texturePosition", POSITION_FRAGMENT, positionTexture);
    this.velocityVariable = this.gpu.addVariable("textureVelocity", VELOCITY_FRAGMENT, velocityTexture);
    this.glowVariable = this.gpu.addVariable("textureGlow", GLOW_FRAGMENT, glowTexture);
    this.emitterIdVariable = this.gpu.addVariable("textureEmitterId", EMITTERID_FRAGMENT, emitterIdTexture);

    this.gpu.setVariableDependencies(this.positionVariable, [
      this.positionVariable,
      this.velocityVariable,
      this.glowVariable,
      this.emitterIdVariable,
    ]);
    this.gpu.setVariableDependencies(this.velocityVariable, [
      this.positionVariable,
      this.velocityVariable,
      this.glowVariable,
      this.emitterIdVariable,
    ]);
    this.gpu.setVariableDependencies(this.glowVariable, [
      this.glowVariable,
      this.velocityVariable,
      this.positionVariable,
    ]);
    this.gpu.setVariableDependencies(this.emitterIdVariable, [this.emitterIdVariable]);

    this.gravityUniform = { value: GRAVITY.clone() };
    this.particleRadiusUniform = { value: PARTICLE_WORLD_SIZE / 2 };
    this.collisionEnabledUniform = { value: 1 };
    Object.assign(this.positionVariable.material.uniforms, {
      uDt: { value: 0 },
      uGravity: this.gravityUniform,
      uParticleRadius: this.particleRadiusUniform,
      uCollisionEnabled: this.collisionEnabledUniform,
      uEmitterCount: this.emitterCountUniform,
      uEmitterPos: this.emitterPosUniform,
      uEmitStart: this.emitStartUniform,
      uEmitCount: this.emitCountUniform,
      uEmitBounce: this.emitBounceUniform,
      uEmitFriction: this.emitFrictionUniform,
      uZLock: { value: 1 },
      uFallKillDist: { value: FALL_KILL_DIST_OFF },
      uFallKillLifeDiv: { value: FALL_KILL_LIFE_DIV_DEFAULT },
      ...collisionUniforms,
    });
    Object.assign(this.velocityVariable.material.uniforms, {
      uDt: { value: 0 },
      uClockTimeWrapped: { value: 0 },
      uGravity: this.gravityUniform,
      uParticleRadius: this.particleRadiusUniform,
      uCollisionEnabled: this.collisionEnabledUniform,
      uEmitterCount: this.emitterCountUniform,
      uEmitStart: this.emitStartUniform,
      uEmitCount: this.emitCountUniform,
      uEmitCone: this.emitConeUniform,
      uEmitQuat: this.emitQuatUniform,
      uEmitSpeedMin: this.emitSpeedMinUniform,
      uEmitSpeedMax: this.emitSpeedMaxUniform,
      uEmitBounce: this.emitBounceUniform,
      uEmitFriction: this.emitFrictionUniform,
      uEmitDrag: this.emitDragUniform,
      uZLock: { value: 1 },
      ...collisionUniforms,
    });
    Object.assign(this.glowVariable.material.uniforms, {
      uDt: { value: 0 },
      uEmitterCount: this.emitterCountUniform,
      uEmitStart: this.emitStartUniform,
      uEmitCount: this.emitCountUniform,
      uEmitLifeMin: this.emitLifeMinUniform,
      uEmitLifeMax: this.emitLifeMaxUniform,
    });
    Object.assign(this.emitterIdVariable.material.uniforms, {
      uEmitterCount: this.emitterCountUniform,
      uEmitStart: this.emitStartUniform,
      uEmitCount: this.emitCountUniform,
      uEmitterPos: this.emitterPosUniform,
    });

    const error = this.gpu.init();
    if (error !== null) {
      throw new Error(`GPUComputationRenderer init failed: ${error}`);
    }

    this.renderMaterial = new THREE.ShaderMaterial({
      uniforms: {
        texturePosition: { value: null },
        textureGlow: { value: null },
        uPointWorldSize: { value: PARTICLE_WORLD_SIZE },
        uPxPerWorld: { value: 1 },
        uFocalDist: { value: VIEW_HEIGHT / (2 * Math.tan((CAMERA_FOV_2D * Math.PI) / 360)) },
        uColor: { value: PARTICLE_COLOR.clone() },
        uLightDir: { value: KEY_LIGHT_DIR.clone() },
        uAmbient: { value: PARTICLE_AMBIENT },
        uSpecular: { value: PARTICLE_SPECULAR },
        uShininess: { value: PARTICLE_SHININESS },
        uRim: { value: PARTICLE_RIM },
        uGlowColor: { value: PARTICLE_GLOW_COLOR.clone().multiplyScalar(PARTICLE_GLOW_PEAK) },
        uOpacity: { value: 1 },
      },
      vertexShader: RENDER_VERTEX,
      fragmentShader: RENDER_FRAGMENT,
      transparent: false,
      depthTest: true,
      depthWrite: true,
      blending: THREE.NormalBlending,
    });

    this.points = new THREE.Points(buildReferenceGeometry(), this.renderMaterial);
    this.points.frustumCulled = false;

    const contactRTType = this.contactFloatCaps.floatBlend && !CONTACT_FORCE_HALF ? THREE.FloatType : THREE.HalfFloatType;
    const makeContactRT = (): THREE.WebGLRenderTarget =>
      new THREE.WebGLRenderTarget(MAX_OBJECTS, 1, {
        type: contactRTType,
        format: THREE.RGBAFormat,
        minFilter: THREE.NearestFilter,
        magFilter: THREE.NearestFilter,
        depthBuffer: false,
        stencilBuffer: false,
      });
    this.contactRTs = Array.from({ length: CONTACT_RING_SIZE }, (): THREE.WebGLRenderTarget => makeContactRT());
    this.scatterMaterial = new THREE.ShaderMaterial({
      uniforms: { textureVelocity: { value: null } },
      vertexShader: SCATTER_VERTEX,
      fragmentShader: SCATTER_FRAGMENT,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: THREE.CustomBlending,
      blendSrc: THREE.OneFactor,
      blendDst: THREE.OneFactor,
    });
    this.scatterPoints = new THREE.Points(buildReferenceGeometry(), this.scatterMaterial);
    this.scatterPoints.frustumCulled = false;
    this.scatterScene = new THREE.Scene();
    this.scatterScene.add(this.scatterPoints);
    this.scatterCamera = new THREE.Camera();
    this.contactSlotPixels = Array.from({ length: CONTACT_RING_SIZE }, (): Float32Array => new Float32Array(MAX_OBJECTS * 4));
    this.contactSlotByteViews = this.contactSlotPixels.map((p: Float32Array): Uint8Array => new Uint8Array(p.buffer));
    this.contactSlotCounts = Array.from({ length: CONTACT_RING_SIZE }, (): Float32Array => new Float32Array(MAX_OBJECTS));
    this.contactSlotImpacts = Array.from({ length: CONTACT_RING_SIZE }, (): Float32Array => new Float32Array(MAX_OBJECTS));
    this.contactSlotRailTSums = Array.from({ length: CONTACT_RING_SIZE }, (): Float32Array => new Float32Array(MAX_OBJECTS));
    this.contactSlotRailCounts = Array.from({ length: CONTACT_RING_SIZE }, (): Float32Array => new Float32Array(MAX_OBJECTS));
    this.contactSlotInFlight = new Array<boolean>(CONTACT_RING_SIZE).fill(false);
    this.contactSlotSubsteps = new Array<number>(CONTACT_RING_SIZE).fill(0);
    this.contactSlotDeliverSubsteps = new Array<number>(CONTACT_RING_SIZE).fill(0);
    this.contactSlotSimTime = new Array<number>(CONTACT_RING_SIZE).fill(0);
    this.contactResolvedQueue = [];
    this.contactCounts = new Float32Array(MAX_OBJECTS);
    this.contactImpacts = new Float32Array(MAX_OBJECTS);
    this.contactRailTSums = new Float32Array(MAX_OBJECTS);
    this.contactRailCounts = new Float32Array(MAX_OBJECTS);
    this.contactWriteIndex = 0;

    this.contactReadPack = CONTACT_READBACK_PACK;
    if (this.contactReadPack) {
      this.buildContactEncodePass();
    }
    console.log(`Contact readback mode=${this.contactReadPack ? "pack" : "async"}`);

    this.updatePointScale();
  }

  /**
   * Builds the float-to-RGBA8 encode pass used when the float readback is
   * unusable: a full-width quad packing each object's four float channels into
   * four RGBA8 texels (see CONTACT_ENCODE_FRAGMENT). The encode RT is read with
   * UNSIGNED_BYTE readPixels, which every platform supports.
   */
  private buildContactEncodePass(): void {
    this.contactEncodeRT = new THREE.WebGLRenderTarget(MAX_OBJECTS * 4, 1, {
      type: THREE.UnsignedByteType,
      format: THREE.RGBAFormat,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      depthBuffer: false,
      stencilBuffer: false,
    });
    this.contactEncodeMaterial = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      uniforms: { uFloatTex: { value: null } },
      vertexShader: FULLSCREEN_QUAD_VERTEX,
      fragmentShader: CONTACT_ENCODE_FRAGMENT,
    });
    this.contactEncodeScene = new THREE.Scene();
    this.contactEncodeScene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.contactEncodeMaterial));
  }

  /**
   * Debug-only switch (?debug Perf section) that freezes the GPGPU simulation
   * to isolate its GPU cost. Both contact buffers are still cleared so audio
   * does not retrigger from the last computed frame while paused.
   */
  setDebugSimPaused(paused: boolean): void {
    this.debugSimPaused = paused;
    if (paused) {
      this.invalidateContactRead();
    }
  }

  /**
   * Discards every in-flight asynchronous contact read (each resolve sees the
   * bumped generation and bails without queueing), drops any resolved-but-undelivered
   * windows from the queue, and zeroes the contact arrays so consumers read
   * silence immediately, rather than a stale read landing one to two frames after
   * a reset or pause clears the histograms.
   */
  private invalidateContactRead(): void {
    this.contactReadGen += 1;
    this.contactResolvedQueue.length = 0;
    this.lastDeliveredSubsteps = 0;
    this.lastDeliveredSimTime = this.elapsed;
    this.contactCounts.fill(0);
    this.contactImpacts.fill(0);
    this.contactRailTSums.fill(0);
    this.contactRailCounts.fill(0);
  }

  /**
   * Debug-only switch (?debug Perf section) that skips the per-frame
   * readRenderTargetPixels GPU sync to isolate its stall cost. Contacts read
   * as zero, so no sound triggers while active — measurement only.
   */
  setDebugReadbackOff(off: boolean): void {
    this.debugReadbackOff = off;
  }

  /**
   * Debug-only switch (?debug Perf section) selecting the collider candidate set:
   * on = the spatial grid + always-test list (production), off = the brute-force
   * all-collider scan. The two paths share the same physics, so flipping it must
   * not change particle trajectories or contacts — the A/B bit-match safety net
   * for the grid traversal. uUseGrid is one shared uniform object across the
   * position and velocity materials, so a single write drives both.
   */
  setUseGrid(useGrid: boolean): void {
    this.positionVariable.material.uniforms.uUseGrid.value = useGrid ? 1 : 0;
  }

  /**
   * Bench-only switch that skips all SDF collision evaluation (resolveCollision
   * and the standalone contact-release nearestRestitution) so the toggle-diff
   * method can attribute the collision/SDF cost. uCollisionEnabled is one shared
   * uniform object across the position and velocity materials, so a single write
   * drives both. Default 1.0 (= enabled) is bit-identical to the pre-toggle
   * shader; only a 0 flip changes behaviour, never normal play.
   */
  setCollisionEnabled(enabled: boolean): void {
    this.collisionEnabledUniform.value = enabled ? 1 : 0;
  }

  /**
   * Debug-only A/B harness: resets the sim, advances `steps` fixed substeps
   * deterministically (clock from 0, seeded emit — no wall-clock input), then
   * folds the position render target into an FNV-1a hash. The grid and
   * brute-force candidate paths share identical physics, so the hash must match
   * across a uUseGrid flip for the same step count — the mechanical proof that
   * the spatial grid drops no contacts. Destructive (it resets), so it is wired
   * only under ?debug. Returns the hash as hex. firstEverEmit is forced true so
   * each replay deterministically fires the t=0 beat regardless of prior runs.
   */
  debugSimChecksum(steps: number): string {
    this.reset();
    this.firstEverEmit = true;
    for (let i = 0; i < steps; i++) {
      this.update(FIXED_DT);
    }
    const rt = this.gpu.getCurrentRenderTarget(this.positionVariable);
    const buf = new Float32Array(PARTICLE_TEXTURE_WIDTH * PARTICLE_TEXTURE_WIDTH * 4);
    this.renderer.readRenderTargetPixels(rt, 0, 0, PARTICLE_TEXTURE_WIDTH, PARTICLE_TEXTURE_WIDTH, buf);
    const bytes = new Uint8Array(buf.buffer);
    let h = 0x811c9dc5;
    for (let i = 0; i < bytes.length; i++) {
      h ^= bytes[i];
      h = Math.imul(h, 0x01000193);
    }
    return (h >>> 0).toString(16);
  }

  /**
   * Reads back every live particle's current xy from the position render target
   * (a particle is live when its age has not yet passed its parked-offscreen
   * sentinel). Debug-only trajectory inspection for the title screen tuner; the
   * per-call GPU readback makes it unsuitable for the render loop.
   */
  debugLivePositions(): { x: number; y: number }[] {
    const rt = this.gpu.getCurrentRenderTarget(this.positionVariable);
    const buf = new Float32Array(PARTICLE_TEXTURE_WIDTH * PARTICLE_TEXTURE_WIDTH * 4);
    this.renderer.readRenderTargetPixels(rt, 0, 0, PARTICLE_TEXTURE_WIDTH, PARTICLE_TEXTURE_WIDTH, buf);
    const out: { x: number; y: number }[] = [];
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const k = i * 4;
      if (Math.abs(buf[k]) < 1e5) {
        out.push({ x: buf[k], y: buf[k + 1] });
      }
    }
    return out;
  }

  /**
   * Contact histograms use an N-slot ring rotated at read-issue, not per frame
   * (see kickContactRead). update() accumulates onsets into the active write
   * slot every frame; a slot is cleared only when it next becomes the write target
   * after a read rotates writes away from it. readContactCounts() issues one read
   * per frame and rotates to the next free slot, so each frame's accumulation
   * window is delivered on its own — the per-frame onset cadence a single-in-flight
   * gate collapsed. While paused, every slot and the contact arrays are cleared so
   * reads stay silent.
   */
  update(realDelta: number): void {
    if (this.debugSimPaused) {
      this.accumulator = 0;
      for (let i = 0; i < this.contactRTs.length; i++) {
        this.contactSlotSubsteps[i] = 0;
        this.clearContactHistogram(this.contactRTs[i]);
      }
      this.invalidateContactRead();
      return;
    }
    this.accumulator += Math.min(realDelta, MAX_DELTA);

    this.positionVariable.material.uniforms.uDt.value = FIXED_DT;
    this.velocityVariable.material.uniforms.uDt.value = FIXED_DT;
    this.glowVariable.material.uniforms.uDt.value = FIXED_DT;

    let firstStep = true;
    let substeps = 0;
    while (this.accumulator >= FIXED_DT) {
      this.elapsed += FIXED_DT;
      this.velocityVariable.material.uniforms.uClockTimeWrapped.value = wrappedClockTime(this.elapsed, this.clockPeriodSec);

      this.advanceEmit(firstStep);
      firstStep = false;

      this.gpu.compute();
      this.accumulateContactHistogram();
      this.accumulator -= FIXED_DT;
      substeps++;
    }
    this.contactSlotSubsteps[this.contactWriteIndex] += substeps;

    this.renderMaterial.uniforms.texturePosition.value =
      this.gpu.getCurrentRenderTarget(this.positionVariable).texture;
    this.renderMaterial.uniforms.textureGlow.value =
      this.gpu.getCurrentRenderTarget(this.glowVariable).texture;
  }

  private clearContactHistogram(rt: THREE.WebGLRenderTarget): void {
    const prevTarget = this.renderer.getRenderTarget();
    const prevColor = new THREE.Color();
    this.renderer.getClearColor(prevColor);
    const prevAlpha = this.renderer.getClearAlpha();

    this.renderer.setRenderTarget(rt);
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.clear(true, false, false);

    this.renderer.setRenderTarget(prevTarget);
    this.renderer.setClearColor(prevColor, prevAlpha);
  }

  private accumulateContactHistogram(): void {
    this.scatterMaterial.uniforms.textureVelocity.value =
      this.gpu.getCurrentRenderTarget(this.velocityVariable).texture;

    const prevTarget = this.renderer.getRenderTarget();
    const prevAutoClear = this.renderer.autoClear;

    this.renderer.setRenderTarget(this.contactRTs[this.contactWriteIndex]);
    this.renderer.autoClear = false;
    this.renderer.render(this.scatterScene, this.scatterCamera);

    this.renderer.autoClear = prevAutoClear;
    this.renderer.setRenderTarget(prevTarget);
  }

  /**
   * Delivers one resolved accumulation window for this frame and kicks the next
   * asynchronous read. The read is non-blocking (readRenderTargetPixelsAsync
   * copies into a PBO at issue time, then resolves off the GPU fence) so the
   * per-frame readPixels GPU-sync stall — ~11ms of CPU block on a mobile tiler,
   * the dominant cost when particles are active — no longer lands on the frame.
   *
   * The ring issues one read per frame, so windows resolve and are delivered one
   * per frame: each frame's onsets become their own batch instead of several
   * frames collapsing into one (the single-in-flight gate dropped rapid-fire
   * onsets that way). Onsets (counts/impacts) are delivered exactly once per
   * window — zeroed on a frame with no resolved window so a held batch never
   * re-fires. railTSums/railCounts are sustained-contact *state*, so they (and
   * the substep gate substepsLastFrame reads) are held at the last delivered
   * window across frames that deliver nothing — a sustained contact persists.
   *
   * windowSim is the sim clock (elapsed) at the end of the delivered window's
   * accumulation — fixed-step derived, so it is independent of which frame the
   * async read resolves on. The audio onset scheduler maps it through the
   * sim↔ctx clock anchor to reserve a fire time decoupled from frame arrival.
   */
  readContactCounts(): {
    counts: Float32Array;
    impacts: Float32Array;
    railTSums: Float32Array;
    railCounts: Float32Array;
    windowSim: number;
  } {
    if (this.debugReadbackOff) {
      this.contactCounts.fill(0);
      this.contactImpacts.fill(0);
      this.contactRailTSums.fill(0);
      this.contactRailCounts.fill(0);
      this.lastDeliveredSubsteps = 0;
      this.lastDeliveredSimTime = this.elapsed;
      return {
        counts: this.contactCounts,
        impacts: this.contactImpacts,
        railTSums: this.contactRailTSums,
        railCounts: this.contactRailCounts,
        windowSim: this.lastDeliveredSimTime,
      };
    }
    const count = Math.min(this.positionVariable.material.uniforms.uObjCount.value, MAX_OBJECTS);
    if (count <= 0) {
      this.contactResolvedQueue.length = 0;
      this.contactCounts.fill(0);
      this.contactImpacts.fill(0);
      this.contactRailTSums.fill(0);
      this.contactRailCounts.fill(0);
      this.lastDeliveredSubsteps = 0;
      this.lastDeliveredSimTime = this.elapsed;
      return {
        counts: this.contactCounts,
        impacts: this.contactImpacts,
        railTSums: this.contactRailTSums,
        railCounts: this.contactRailCounts,
        windowSim: this.lastDeliveredSimTime,
      };
    }
    this.deliverContactWindow();
    this.kickContactRead(count);
    return {
      counts: this.contactCounts,
      impacts: this.contactImpacts,
      railTSums: this.contactRailTSums,
      railCounts: this.contactRailCounts,
      windowSim: this.lastDeliveredSimTime,
    };
  }

  /**
   * Pops the oldest resolved window off the FIFO and copies its decoded onsets,
   * rail state and substep count into the consumer arrays. With nothing resolved
   * this frame the onsets are zeroed (an onset window is never replayed) while
   * the rail state and substep gate are held at the previous delivery so a
   * sustained contact does not flicker silent between resolves. A delivered slot
   * is now free for the next write rotation.
   */
  private deliverContactWindow(): void {
    if (this.contactResolvedQueue.length === 0) {
      this.contactCounts.fill(0);
      this.contactImpacts.fill(0);
      return;
    }
    const slot = this.contactResolvedQueue.shift() as number;
    this.contactCounts.set(this.contactSlotCounts[slot]);
    this.contactImpacts.set(this.contactSlotImpacts[slot]);
    this.contactRailTSums.set(this.contactSlotRailTSums[slot]);
    this.contactRailCounts.set(this.contactSlotRailCounts[slot]);
    this.lastDeliveredSubsteps = this.contactSlotDeliverSubsteps[slot];
    this.lastDeliveredSimTime = this.contactSlotSimTime[slot];
  }

  /**
   * Issues a non-blocking pixel read of the current write slot's accumulation
   * window, then rotates writes to the next slot (cleared so it starts a fresh
   * window) and resets its substep counter. The rotation target must be free —
   * not still being GPU-read and not holding a resolved-but-undelivered window —
   * so the read never races a clear or a write and no decoded window is
   * overwritten before delivery. When the ring is full (the next slot is busy),
   * the read is skipped this frame and accumulation continues in the current
   * slot; that frame's contacts then merge into the next window — the last-resort
   * cadence loss under a GPU stall, in preference to dropping onsets.
   *
   * On resolution the window is decoded into the slot's own arrays (railTSums/
   * railCounts are summed sustained state divided by the window's substeps, so
   * their ratio — mean arc position t — is normalization-independent; counts/
   * impacts are onset sums, not normalized) and queued for delivery. A generation
   * token captured at issue time discards a read straddling reset()/pause. The
   * read spans only the live collider count (≤ MAX_OBJECTS).
   */
  private kickContactRead(count: number): void {
    const nextIndex = (this.contactWriteIndex + 1) % this.contactRTs.length;
    if (this.contactSlotInFlight[nextIndex] || this.contactResolvedQueue.includes(nextIndex)) {
      return;
    }
    const readIndex = this.contactWriteIndex;
    const readSubsteps = this.contactSlotSubsteps[readIndex];
    this.contactSlotSimTime[readIndex] = this.elapsed;
    const gen = this.contactReadGen;
    const pixels = this.contactSlotPixels[readIndex];
    this.contactSlotInFlight[readIndex] = true;
    this.contactWriteIndex = nextIndex;
    this.clearContactHistogram(this.contactRTs[nextIndex]);
    this.contactSlotSubsteps[nextIndex] = 0;
    if (this.contactReadPack) {
      this.contactSlotInFlight[readIndex] = false;
      this.readPackedContactWindow(readIndex, count, readSubsteps, gen);
      return;
    }
    this.renderer
      .readRenderTargetPixelsAsync(this.contactRTs[readIndex], 0, 0, count, 1, pixels)
      .then((): void => {
        this.contactSlotInFlight[readIndex] = false;
        this.decodeContactWindow(readIndex, count, readSubsteps, gen);
      })
      .catch((): void => {
        this.contactSlotInFlight[readIndex] = false;
      });
  }

  /**
   * Encodes the read slot's float window to RGBA8, reads it back with
   * UNSIGNED_BYTE readPixels (the byte view aliases the slot's Float32Array, so
   * the little-endian packed bytes reconstruct the floats in place), then decodes
   * it. The iOS path, where the float RT cannot be read directly. Synchronous —
   * the encode pass must complete before the read.
   */
  private readPackedContactWindow(readIndex: number, count: number, readSubsteps: number, gen: number): void {
    if (this.contactEncodeRT === null || this.contactEncodeScene === null || this.contactEncodeMaterial === null) {
      throw new Error("contact encode pass not built");
    }
    this.contactEncodeMaterial.uniforms.uFloatTex.value = this.contactRTs[readIndex].texture;
    const prevTarget = this.renderer.getRenderTarget();
    this.renderer.setRenderTarget(this.contactEncodeRT);
    this.renderer.render(this.contactEncodeScene, this.contactEncodeCamera);
    this.renderer.setRenderTarget(prevTarget);
    this.renderer.readRenderTargetPixels(this.contactEncodeRT, 0, 0, count * 4, 1, this.contactSlotByteViews[readIndex]);
    this.decodeContactWindow(readIndex, count, readSubsteps, gen);
  }

  /**
   * Decodes a read slot's float pixels into its onset/rail arrays and queues it
   * for delivery, unless a reset/pause bumped the generation since the read was
   * issued (then the window is dropped). Shared by the async resolve and the
   * pack read path so both normalize railTSums/railCounts by the window's own
   * substeps (their ratio — mean arc position — stays normalization-independent)
   * and leave counts/impacts as raw onset sums.
   */
  private decodeContactWindow(readIndex: number, count: number, readSubsteps: number, gen: number): void {
    if (gen !== this.contactReadGen) {
      return;
    }
    const norm = readSubsteps > 0 ? 1 / readSubsteps : 0;
    const pixels = this.contactSlotPixels[readIndex];
    const counts = this.contactSlotCounts[readIndex];
    const impacts = this.contactSlotImpacts[readIndex];
    const railTSums = this.contactSlotRailTSums[readIndex];
    const railCounts = this.contactSlotRailCounts[readIndex];
    counts.fill(0);
    impacts.fill(0);
    railTSums.fill(0);
    railCounts.fill(0);
    for (let i = 0; i < count; i++) {
      counts[i] = pixels[i * 4];
      impacts[i] = pixels[i * 4 + 1];
      railTSums[i] = pixels[i * 4 + 2] * norm;
      railCounts[i] = pixels[i * 4 + 3] * norm;
    }
    this.contactSlotDeliverSubsteps[readIndex] = readSubsteps;
    this.contactResolvedQueue.push(readIndex);
  }

  /**
   * Substeps over the window delivered this frame (held at the last delivery on a
   * frame that delivered nothing, so a sustained contact's rail voice keeps
   * processing between resolves). Zero when that window ran entirely faster than
   * FIXED_DT and never accumulated — rail voice consumers skip those frames
   * instead of reading a false silence.
   */
  substepsLastFrame(): number {
    return this.lastDeliveredSubsteps;
  }

  /**
   * Replaces the active emitter set. Each emitter's per-frame spawn budget,
   * position and initial-velocity parameters are uploaded to the uniform arrays
   * the shaders read; `rotation` is uploaded as a quaternion (the emit shader
   * rotates the +Y base heading by it) and `coneDeg` is converted to radians.
   * Emitters past MAX_EMITTERS are dropped.
   */
  setEmitters(emitters: readonly Emitter[]): void {
    const count = Math.min(emitters.length, MAX_EMITTERS);
    this.emitterCountUniform.value = count;
    for (let e = 0; e < count; e++) {
      const em = emitters[e];
      this.emitterPosUniform.value[e].copy(em.position);
      this.emitQuatUniform.value[e].set(em.rotation.x, em.rotation.y, em.rotation.z, em.rotation.w);
      this.emitConeUniform.value[e] = em.coneDeg / DEG;
      this.emitSpeedMinUniform.value[e] = em.speedMin;
      this.emitSpeedMaxUniform.value[e] = em.speedMax;
      this.emitSpeedMinBase[e] = em.speedMin;
      this.emitSpeedMaxBase[e] = em.speedMax;
      this.emitLifeMinUniform.value[e] = em.lifeMin;
      this.emitLifeMaxUniform.value[e] = em.lifeMax;
      this.emitBounceUniform.value[e] = em.bounce;
      this.emitFrictionUniform.value[e] = em.friction;
      this.emitDragUniform.value[e] = em.drag;
      this.emitRates[e] = em.rate;
      this.emitGate[e] = em.gate;
      this.emitOffset[e] = em.offset;
      this.emitClockSync[e] = em.clockSync;
      this.emitSeqMode[e] = em.seqMode;
      this.emitSeqSteps[e] = em.seqSteps;
      this.emitSeqNum[e] = em.num;
    }
  }

  /**
   * Sets, per active emitter index, this frame's external pulse-driven emit
   * count and whether the emitter is driven. A driven emitter ignores its rate
   * and instead spawns `external[e]` particles once on the frame's first fixed
   * substep (the rest of the substeps emit 0), so one inbound pulse yields one
   * particle. gate=0 remains a hard mute in both driven and non-driven paths.
   * Emitters left non-driven keep their rate-phase emit (no regression).
   */
  setEmitInjection(external: readonly number[], driven: readonly boolean[], speed: readonly (number | null)[]): void {
    for (let e = 0; e < MAX_EMITTERS; e++) {
      this.emitExternal[e] = external[e] ?? 0;
      this.emitDriven[e] = driven[e] ?? false;
      this.emitSpeedInj[e] = speed[e] ?? null;
    }
  }

  /**
   * Allocates this fixed step's spawn budget across active emitters into the shared
   * circular buffer. Each emitter claims a contiguous index range so the position /
   * velocity shaders can resolve each spawned particle to exactly one emitter.
   * Per-emitter count is delegated to computeEmitCount (see emitPhase.ts).
   *
   * Emit windows are half-open (tPrev, tNow], so the very first substep after
   * construction widens its window below zero (see emitWindowStart) — otherwise
   * the clock-origin boundary at t=0 (sequencer cell 0, the first beat) would fall
   * outside every window and never fire. The first substep after reset() widens
   * the window only for the sequencer path (firstSeqEmitAfterReset), so a reset
   * replays the seq cell-0 downbeat but not the grid-origin / free-Hz t=0 burst.
   *
   * An emitter that is neither driven nor clock-synced and has rate 0 always
   * yields 0 this step (gatedRate(0)=0 → computeEmitCount(0)=0), so it is
   * short-circuited to count 0 without advancing the cursor — bit-identical to
   * the full path, just skipping the unread speed-uniform writes.
   */
  private advanceEmit(firstStep: boolean): void {
    const count = this.emitterCountUniform.value;
    const tNow = this.clockTimeSec();
    const tPrev = emitWindowStart(tNow, FIXED_DT, this.firstEverEmit);
    const tPrevSeq = emitWindowStart(tNow, FIXED_DT, this.firstEverEmit || this.firstSeqEmitAfterReset);
    this.firstEverEmit = false;
    this.firstSeqEmitAfterReset = false;
    const cell = cellSec(this.clockPeriodSec);
    let cursor = this.nextEmitIndex;
    let total = 0;
    for (let e = 0; e < count; e++) {
      if (!this.emitDriven[e] && !this.emitClockSync[e] && this.emitRates[e] === 0) {
        this.emitStartUniform.value[e] = cursor;
        this.emitCountUniform.value[e] = 0;
        continue;
      }
      const inj = this.emitDriven[e] ? this.emitSpeedInj[e] : null;
      if (inj !== null) {
        this.emitSpeedMinUniform.value[e] = inj * (1 - INJ_SPEED_SPREAD / 2);
        this.emitSpeedMaxUniform.value[e] = inj * (1 + INJ_SPEED_SPREAD / 2);
      } else {
        this.emitSpeedMinUniform.value[e] = this.emitSpeedMinBase[e];
        this.emitSpeedMaxUniform.value[e] = this.emitSpeedMaxBase[e];
      }
      let c: number;
      if (this.emitDriven[e]) {
        c = this.emitGate[e] <= 0 ? 0 : firstStep ? this.emitExternal[e] : 0;
      } else if (this.emitClockSync[e]) {
        c = this.emitSeqMode[e]
          ? computeSeqEmit(cell, this.emitSeqNum[e], this.emitSeqSteps[e], tPrevSeq, tNow)
          : computeGridEmitCount(cell, 1, this.emitOffset[e], tPrev, tNow);
        if (ONSET_DIAG && this.emitSeqMode[e] && c > 0) {
          recordSeqEmit(tNow);
        }
      } else {
        c = computeEmitCount(gatedRate(this.emitRates[e], this.emitGate[e]), this.emitOffset[e], tPrev, tNow);
      }
      if (total + c > PARTICLE_COUNT) {
        c = PARTICLE_COUNT - total;
      }
      total += c;
      this.emitStartUniform.value[e] = cursor;
      this.emitCountUniform.value[e] = c;
      cursor = (cursor + c) % PARTICLE_COUNT;
    }
    this.nextEmitIndex = cursor;
  }

  setPointSize(worldSize: number): void {
    this.renderMaterial.uniforms.uPointWorldSize.value = worldSize;
    this.particleRadiusUniform.value = worldSize / 2;
  }

  setMaterial(material: ParticleMaterial): void {
    const params = PARTICLE_MATERIAL_PARAMS[material];
    this.renderMaterial.uniforms.uAmbient.value = params.ambient;
    this.renderMaterial.uniforms.uSpecular.value = params.specular;
    this.renderMaterial.uniforms.uShininess.value = params.shininess;
    this.renderMaterial.uniforms.uRim.value = params.rim;
  }

  setGravityY(y: number): void {
    this.gravityUniform.value.y = y;
  }

  /**
   * Title-screen only: recolors the point sprites for a light background — a
   * flat dark disk with no specular / rim / glow, so particles read as solid
   * black on white instead of the editor's lit blue spheres.
   */
  setFlatColor(color: THREE.Color): void {
    this.renderMaterial.uniforms.uColor.value.copy(color);
    this.renderMaterial.uniforms.uSpecular.value = 0;
    this.renderMaterial.uniforms.uRim.value = 0;
    this.renderMaterial.uniforms.uAmbient.value = 0.85;
    this.renderMaterial.uniforms.uGlowColor.value.setScalar(0);
  }

  /**
   * Title-screen only: fades the point sprites via the render shader's alpha.
   * transparent is toggled on only below full opacity so the editor path (never
   * calling this, uOpacity=1) stays bit-identical to the opaque pre-alpha shader.
   */
  setOpacity(opacity: number): void {
    this.renderMaterial.uniforms.uOpacity.value = opacity;
    const transparent = opacity < 1;
    if (this.renderMaterial.transparent !== transparent) {
      this.renderMaterial.transparent = transparent;
      this.renderMaterial.needsUpdate = true;
    }
  }

  /**
   * The slider maximum means "off": the max (100) is still reachable by a fast
   * particle under strong gravity within its lifespan, so it maps to a distance
   * no particle can ever fall.
   */
  setFallKillDistance(d: number): void {
    this.positionVariable.material.uniforms.uFallKillDist.value = d >= FALL_KILL_DIST_MAX ? FALL_KILL_DIST_OFF : d;
  }

  setFallKillLifeDivisor(n: number): void {
    this.positionVariable.material.uniforms.uFallKillLifeDiv.value = n;
  }

  setClockPeriod(sec: number): void {
    this.clockPeriodSec = sec;
  }

  /**
   * Kills every particle and rewinds the clock to 0 so the sequencer plays from
   * its first cell. Both ping-pong targets of each GPU variable are rewritten
   * from its initialValueTexture; otherwise the stale alternate target would
   * resurrect pre-reset state on the next compute. firstSeqEmitAfterReset arms
   * the seq-only t=0 window so the next step replays the cell-0 downbeat. Every
   * contact histogram slot is cleared and invalidateContactRead() bumps the
   * generation so in-flight reads bail and queued windows are dropped — leaving
   * them would replay pre-reset contacts as a spurious pulse / audio trigger on
   * the first post-reset frame.
   */
  reset(): void {
    this.elapsed = 0;
    this.accumulator = 0;
    this.nextEmitIndex = 0;
    this.firstSeqEmitAfterReset = true;
    for (let e = 0; e < MAX_EMITTERS; e++) {
      this.emitExternal[e] = 0;
      this.emitDriven[e] = false;
      this.emitSpeedInj[e] = null;
    }
    for (let i = 0; i < this.contactRTs.length; i++) {
      this.clearContactHistogram(this.contactRTs[i]);
      this.contactSlotSubsteps[i] = 0;
    }
    this.invalidateContactRead();
    for (const variable of [this.positionVariable, this.velocityVariable, this.glowVariable, this.emitterIdVariable]) {
      this.gpu.renderTexture(variable.initialValueTexture, variable.renderTargets[0]);
      this.gpu.renderTexture(variable.initialValueTexture, variable.renderTargets[1]);
    }
  }

  /**
   * Anchors the clock to a nonzero elapsed value so a late joiner lands mid-pattern
   * at the room's shared origin instead of replaying from cell 0. Unlike reset(),
   * this must NOT arm either window-widening flag: firstEverEmit (construction) and
   * firstSeqEmitAfterReset (reset, just set by the join's applySceneInPlace) both
   * widen the first emit window below zero, which at a large elapsed would burst the
   * whole pattern's active cells at once. Cleared here, the next substep uses the
   * normal (tNow - FIXED_DT) window and the clock advances by FIXED_DT as usual.
   */
  setClockTimeBase(sec: number): void {
    this.elapsed = sec;
    this.firstEverEmit = false;
    this.firstSeqEmitAfterReset = false;
  }

  /**
   * The clock time base in seconds: sim `elapsed`, starting at 0 so the sequencer
   * always plays from its first cell. The single source every phase / orbit /
   * emit-grid consumer reads. Read once per frame for orbit and the sequencer
   * playhead.
   */
  clockTimeSec(): number {
    return this.elapsed;
  }

  /**
   * Phase of the room clock in [0,1), driven by clockTimeSec(). The first
   * phase-locked consumer of the clock; later consumers compose their own period /
   * offset via phaseAt.
   */
  clockPhase(): number {
    return phaseAt(this.clockTimeSec(), this.clockPeriodSec, 0);
  }

  clockPeriodSeconds(): number {
    return this.clockPeriodSec;
  }

  setZLock(value: number): void {
    this.positionVariable.material.uniforms.uZLock.value = value;
    this.velocityVariable.material.uniforms.uZLock.value = value;
  }

  setFocalDist(value: number): void {
    this.renderMaterial.uniforms.uFocalDist.value = value;
  }

  updatePointScale(): void {
    const heightPx = this.feedHeightPx ?? this.renderer.getDrawingBufferSize(new THREE.Vector2()).y;
    this.renderMaterial.uniforms.uPxPerWorld.value = heightPx / VIEW_HEIGHT;
  }

  /**
   * Overrides the point-size pixels-per-world basis with the Community feed
   * card's physical pixel height while a focused scene renders into the card
   * rect (the canvas backing store stays full-screen, so the drawing-buffer
   * height would otherwise size points for the whole screen and blow them up
   * inside the smaller card box). null restores the full drawing-buffer basis.
   */
  setFeedHeightPx(heightPx: number | null): void {
    this.feedHeightPx = heightPx;
    this.updatePointScale();
  }

  dispose(): void {
    this.gpu.dispose();
    this.points.geometry.dispose();
    this.renderMaterial.dispose();
    this.scatterMaterial.dispose();
    this.scatterPoints.geometry.dispose();
    for (const rt of this.contactRTs) {
      rt.dispose();
    }
    this.contactEncodeRT?.dispose();
    this.contactEncodeMaterial?.dispose();
    this.contactEncodeScene?.children.forEach((c: THREE.Object3D): void => {
      if (c instanceof THREE.Mesh) {
        c.geometry.dispose();
      }
    });
  }
}
